import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Loader, Pencil, Plus, Search } from "lucide-react";
import CustomSelect from "../../../components/ui/CustomSelect";
import { usePayoutPartners, usePayoutMethods } from "../hooks/usePayoutPartners";
import { useCurrencies } from "../../currency-settings/hooks/useCurrencies";
import { buildCurrencyOptions, parseCurrencyOption } from "../../../utils/currencyOptions";
import PayoutPartnerEditForm from "./PayoutPartnerEditForm";
import PayoutPartnerAddForm from "./PayoutPartnerAddForm";

export function PayoutPartnerTable() {
  // ?search= pre-fills the search (the fee margin page links here with a partner).
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("search") ?? "");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { currencyCode, countryCode: filterCountryCode } = parseCurrencyOption(currencyFilter);
  const [payoutMethodUuid, setPayoutMethodUuid] = useState("");
  const { data, isLoading, isError, error } = usePayoutPartners(
    search.trim() || undefined,
    currencyCode || undefined,
    payoutMethodUuid || undefined
  );
  const { data: currenciesResponse } = useCurrencies();
  const currencies = currenciesResponse?.data || [];
  const currencyOptions = buildCurrencyOptions(currencies);
  const { data: payoutMethodsResponse } = usePayoutMethods(currencyCode || undefined);
  const payoutMethods = payoutMethodsResponse?.data || [];
  const payoutMethodOptions = [
    { label: "All Payout Methods", value: "" },
    ...payoutMethods.map((m) => ({
      value: m.uuid,
      label: m.name,
    })),
  ];

  const statusOptions = [
    { label: "All Statuses", value: "" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const handleCurrencyChange = (value) => {
    setCurrencyFilter(value);
    // The previously-selected payout method may not exist for the newly
    // selected currency, so reset it rather than silently filtering on a
    // method that no longer applies.
    setPayoutMethodUuid("");
  };
  const [selected, setSelected] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const handleEdit = (row) => {
    setSelected(row);
    setIsEditOpen(true);
  };

  const columns = [
    {
      id: "currencyCode",
      header: "Currency",
      accessorFn: (row) => row.currencyCode,
      cell: (info) => (
        <span className="font-semibold text-gray-800 uppercase">{info.getValue()}</span>
      ),
    },
    {
      id: "countryCode",
      header: "Country",
      accessorFn: (row) => row.countryCode,
      cell: (info) => <span className="text-sm text-gray-600 uppercase">{info.getValue()}</span>,
    },
    {
      id: "method",
      header: "Payout Method",
      accessorFn: (row) => row.businessPayoutMethod?.name,
      cell: (info) => <span className="text-sm text-gray-600">{info.getValue() || "—"}</span>,
    },
    {
      id: "provider",
      header: "Provider",
      accessorFn: (row) => row.businessPayoutsProvider?.name,
      cell: (info) => (
        <span className="text-sm font-medium text-gray-700 capitalize">
          {info.getValue() || "—"}
        </span>
      ),
    },
    {
      id: "priority",
      header: "Priority",
      accessorFn: (row) => row.priority,
      cell: (info) => (
        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
          {info.getValue()}
        </span>
      ),
    },
    {
      id: "fee",
      header: "Fee",
      accessorFn: (row) => row,
      cell: (info) => {
        const row = info.getValue();
        const value = Number(row.payoutFeeValue ?? 0);
        return (
          <span className="text-sm text-gray-600">
            {row.payoutFeeType === "percentage" ? `${value}%` : value}
          </span>
        );
      },
    },
    {
      id: "isActive",
      header: "Active",
      accessorFn: (row) => row.isActive,
      cell: (info) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
            info.getValue() ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"
          }`}
        >
          {info.getValue() ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <button
          onClick={() => handleEdit(row.original)}
          className="flex gap-1 items-center text-sm font-medium text-emerald-600 hover:text-emerald-800"
        >
          <Pencil size={14} /> Edit
        </button>
      ),
    },
  ];

  const records = useMemo(() => {
    const rows = data?.data || [];
    // The API filters by currency code only; narrow to the chosen country
    // client-side so "XOF — Senegal" doesn't also show Côte d'Ivoire rows.
    const byCountry = filterCountryCode
      ? rows.filter((r) => String(r.countryCode || "").toUpperCase() === filterCountryCode)
      : rows;
    if (!statusFilter) return byCountry;
    return byCountry.filter((r) => Boolean(r.isActive) === (statusFilter === "active"));
  }, [data, filterCountryCode, statusFilter]);

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex gap-3 items-center mb-4">
        <div className="relative max-w-xs w-full">
          <Search className="absolute top-1/2 left-3 w-4 h-4 text-gray-400 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by currency, country, method, or provider"
            className="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div className="w-48">
          <CustomSelect
            value={currencyFilter}
            onChange={handleCurrencyChange}
            options={currencyOptions}
            placeholder="Filter by Currency"
          />
        </div>
        <div className="w-56">
          <CustomSelect
            value={payoutMethodUuid}
            onChange={setPayoutMethodUuid}
            options={payoutMethodOptions}
            placeholder="Filter by Payout Method"
          />
        </div>
        <div className="w-44">
          <CustomSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            placeholder="Filter by Status"
          />
        </div>
        <button
          onClick={() => setIsAddOpen(true)}
          className="flex gap-1.5 items-center px-3 py-2 ml-auto text-sm font-medium text-white whitespace-nowrap bg-emerald-600 rounded-lg hover:bg-emerald-700"
        >
          <Plus size={16} /> Add Provider
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-[400px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading payout partners <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[300px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50/50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      {search ? `No payout partners match "${search}"` : "No payout partners found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PayoutPartnerEditForm
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        record={selected}
      />
      <PayoutPartnerAddForm isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />
    </div>
  );
}

export default PayoutPartnerTable;
